/*
 * Changes:
 * - Added 'use client' directive for Next.js 13+ compatibility
 * - File contains client-side features: React hooks, context provider, and custom hooks
 */

'use client'

import {createContext, useContext} from 'react'
import PropTypes from 'prop-types'
import {useSimpleQuery} from '@frnt/base/hooks'
import {ResourceApiServiceProvider} from '../resource-api-service-provider'
import qs from 'qs'

const AccountClientTradersServiceContext = createContext(undefined)

const AccountClientTradersServiceProvider = ({
  children,
  baseApiHost,
  handleUnauthorized = () => {
    // Do nothing
  }
}) => {
  return (
    <ResourceApiServiceProvider
      Context={AccountClientTradersServiceContext}
      handleUnauthorized={handleUnauthorized}
      host={baseApiHost}
      valueCallback={(axios) => ({
        create: async (payload) => {
          const {data} = await axios.post(`/api/v1/org/account_client_traders`, payload)
          return data
        },
        list: async (query) => {
          const {data} = await axios.get(
            `/api/v1/org/account_client_traders${qs.stringify(query, {
              addQueryPrefix: true
            })}`
          )
          return data
        },
        get: async (id) => {
          const {data} = await axios.get(`/api/v1/org/account_client_traders/${id}`)
          return data
        },
        update: async (id, payload) => {
          const {data} = await axios.patch(`/api/v1/org/account_client_traders/${id}`, payload)
          return data
        },
        updateRole: async (id, payload) => {
          const {data} = await axios.patch(`/api/v1/org/account_client_traders/${id}/role`, payload)
          return data
        },
        upsertBatch: async (payload) => {
          const {data} = await axios.patch(`/api/v1/org/account_client_traders`, payload)
          return data
        },
        invite: async (payload) => {
          const {data} = await axios.post(`/api/v1/org/account_client_traders/invite`, payload)
          return data
        },
        delete: async (id, payload) => {
          const {data} = await axios.delete(`/api/v1/org/account_client_traders/${id}`, {
            data: payload
          })
          return data
        }
      })}>
      {children}
    </ResourceApiServiceProvider>
  )
}

AccountClientTradersServiceProvider.propTypes = {
  children: PropTypes.any,
  baseApiHost: PropTypes.string.isRequired,
  handleUnauthorized: PropTypes.func
}

const useAccountClientTradersService = () => {
  const context = useContext(AccountClientTradersServiceContext)
  if (context === undefined) {
    throw new Error('AccountClientTradersServiceContext was not provided. Make sure your component is a child of the AccountClientTradersServiceProvider.')
  }
  return context
}

const useAccountClientTradersQuery = ({query, ...otherProps}) => {
  const accountClientTradersService = useAccountClientTradersService()
  return useSimpleQuery({
    queryKey: `account_client_traders-list-${JSON.stringify(query)}`,
    queryFn: async () => accountClientTradersService.list(query),
    ...otherProps
  })
}

const useAccountClientTraderQuery = ({id, query, enabled, ...otherProps}) => {
  const accountClientTradersService = useAccountClientTradersService()
  return useSimpleQuery({
    queryKey: `account_client_traders-get-${id}`,
    queryFn: async () => accountClientTradersService.get(id, query),
    enabled: enabled !== undefined ? enabled : !!id,
    ...otherProps
  })
}

export {AccountClientTradersServiceProvider, useAccountClientTradersService, useAccountClientTradersQuery, useAccountClientTraderQuery}
